import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import ROUTES from '../../../../config/routes'
import { duplicateTransitOperation } from '../api/duplicateTransitOperation'

function useDuplicateTransitOperationModal() {
  const [transitOperationId, setTransitOperationId] = useState<number | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const open = useCallback((id: number) => {
    setTransitOperationId(id)
  }, [])

  const close = useCallback(() => {
    setTransitOperationId(null)
    setError(undefined)
  }, [])

  const submit = useCallback(async (withFiles: boolean) => {
    if (!transitOperationId) {
      throw new Error('Missing transit operation ID')
    }

    try {
      setIsSubmitting(true)
      setError(undefined)

      const transitOperationCopy = await duplicateTransitOperation(transitOperationId, withFiles)

      const isEns = transitOperationCopy.declarationType === 'ENS'
      const redirectPath = isEns ? ROUTES.ens : ROUTES.transitP5
      navigate(`${redirectPath}/${transitOperationCopy.id}`)
      toast.success(t('messages.duplicate', {
        context: 'successful',
        replace: { id: transitOperationCopy.id },
      }))
      close()
    } catch (err) {
      if (err instanceof Error) {
        setError(err)
      } else {
        setError(new Error(String(err)))
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [close, navigate, t, transitOperationId])

  const confirm = useCallback(async () => {
    await submit(true)
  }, [submit])

  const reject = useCallback(async () => {
    await submit(false)
  }, [submit])

  return {
    transitOperationId,
    isOpen: transitOperationId != null,
    open,
    close,
    confirm,
    reject,
    isSubmitting,
    error,
  }
}

export default useDuplicateTransitOperationModal
